import React, { useState } from 'react';
import { Check, X, Undo2, ChevronDown, ChevronRight } from 'lucide-react';
import type { MemoryPromotion } from '@/services/api/agentApi';
import { Button } from '@/components/ui/button';

function confidenceColor(c: number): string {
  if (c >= 0.8) return '#22c55e';
  if (c >= 0.6) return '#f59e0b';
  return '#ef4444';
}

function timeAgo(date: string): string {
  const diff = Date.now() - new Date(date).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

interface Props {
  promotion: MemoryPromotion;
  onApprove?: (id: string, note?: string) => void | Promise<void>;
  onReject?: (id: string, note?: string) => void | Promise<void>;
  onRevoke?: (id: string) => void | Promise<void>;
}

export const PromotionCard: React.FC<Props> = ({ promotion, onApprove, onReject, onRevoke }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const color = confidenceColor(promotion.llm_confidence);

  const isPending = promotion.status === 'pending_review';
  const isLive = promotion.status === 'auto_approved' || promotion.status === 'approved';

  const handle = async (action: () => Promise<void> | void) => {
    setBusy(true);
    try { await action(); } finally { setBusy(false); }
  };

  return (
    <div className="border rounded-lg p-4 mb-3 bg-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${statusBadgeClass(promotion.status)}`}>
              {promotion.status.replace(/_/g, ' ')}
            </span>
            <span className="text-xs text-muted-foreground">
              → {promotion.target_organization_id}
            </span>
            <span className="text-xs text-muted-foreground ml-auto">
              {timeAgo(promotion.decided_at)}
            </span>
          </div>
          <p className="text-sm font-medium leading-tight mb-2">{promotion.synthesized_text}</p>
          <div className="flex items-center gap-2 text-xs">
            <span style={{ color }}>{(promotion.llm_confidence * 100).toFixed(0)}% confidence</span>
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              reasoning
            </button>
          </div>
          {expanded && (
            <div className="mt-2 text-xs text-muted-foreground border-l-2 pl-2">
              {promotion.llm_reasoning}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {isPending && onApprove && (
            <Button size="sm" disabled={busy} onClick={() => handle(() => onApprove(promotion.promotion_id))}>
              <Check className="h-3 w-3 mr-1" /> Approve
            </Button>
          )}
          {isPending && onReject && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => handle(() => onReject(promotion.promotion_id))}>
              <X className="h-3 w-3 mr-1" /> Reject
            </Button>
          )}
          {isLive && onRevoke && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => handle(() => onRevoke(promotion.promotion_id))}>
              <Undo2 className="h-3 w-3 mr-1" /> Revoke
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

function statusBadgeClass(status: MemoryPromotion['status']): string {
  switch (status) {
    case 'pending_review': return 'bg-amber-500/10 text-amber-600';
    case 'auto_approved': return 'bg-blue-500/10 text-blue-600';
    case 'approved': return 'bg-green-500/10 text-green-600';
    case 'rejected': return 'bg-red-500/10 text-red-600';
    case 'revoked': return 'bg-gray-500/10 text-gray-600';
  }
}
